/**
 * Tickets Service - API calls para tickets
 */

import type { AxiosRequestConfig } from 'axios';
import type {
  ActivityAttachment,
  ActivityResponse,
  KanbanBoardResponse,
  KanbanColumnPage,
  TicketExportQuery,
  TicketKanbanBoardAggregates,
  TicketKanbanCard,
  TicketKanbanQuery,
  TicketTimelineResponse,
  TransferRequest,
} from '@troia-v3/types';

import { api } from '@/lib/api';
import type { ContextOperatorFilterUser } from '@/shared/utils/contextOperators';

/**
 * Config axios com o flag lido pelo interceptor global de `src/lib/api.ts`:
 * suprime o `toast.error` genérico quando o caller mostra toast/feedback
 * próprio (padrão da base — ver `leadsService.createLead`,
 * `billingApi.addCard`). Usado nas chamadas do composer de tickets, cujos
 * erros são tratados localmente (toast do composer ou chip "Falhou" + retry).
 */
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};
import type {
  TicketResponse,
  TicketListResponse,
  CreateTicketRequest,
  UpdateTicketRequest,
  TicketQuery
} from '../types';

/**
 * Payload de resposta pública do ticket (composer — aba "Resposta").
 * Mesmo shape de anexo (`ActivityAttachment`) aceito por `/reply` e `/activities`.
 */
export interface TicketReplyPayload {
  content: string;
  attachments?: ActivityAttachment[];
}

/** Payload de nota interna do ticket (composer — aba "Nota Interna"). */
export interface TicketNotePayload {
  content: string;
  ticketTitle?: string;
  attachments?: ActivityAttachment[];
}

/**
 * Resultado do upload genérico (`POST /uploads?folder=X`) já mapeado pro
 * shape de `ActivityAttachment` ({url, filename, size, mimeType}).
 */
export interface TicketUploadResult {
  url: string;
  filename: string;
  size?: number;
  mimeType?: string;
}

/**
 * Erro customizado emitido quando o backend sinaliza que a exportação foi
 * truncada (mais de 10k tickets matcheando o filtro). Callers devem mostrar
 * aviso específico pedindo pro usuário refinar os filtros.
 */
export class ExportTruncatedError extends Error {
  constructor() {
    super('Exportação truncada em 10.000 tickets — refine os filtros.');
    this.name = 'ExportTruncatedError';
  }
}

/**
 * Resultado de `POST /tickets/:id/transfer` — o envelope do backend traz
 * `warnings?: string[]` como IRMÃO de `data` (ausente, não `[]`, quando não há
 * nada a avisar — ver `TicketsController.transfer`), populado quando o
 * responsável atual foi desatribuído automaticamente por não ser mais
 * elegível no pipeline de destino. `TransferTicketDialog` (Task 10) usa
 * `warnings` para exibir aviso pós-transferência.
 */
export interface TicketTransferResult {
  ticket: TicketResponse;
  warnings?: string[];
}

/**
 * Query da página adicional de UMA coluna (stage) do kanban de tickets
 * (`GET /tickets/kanban/column`, Task 5). `@troia-v3/types` só exporta
 * `TicketKanbanQuery` (board) — sem tipo nomeado pra coluna (diferente de
 * `LeadKanbanColumnQuery`) — então estende localmente aqui, espelhando
 * `TicketKanbanColumnQuerySchema` do backend (`TicketKanbanQuerySchema.extend({stageId, cursor})`).
 */
export interface TicketKanbanColumnQuery extends TicketKanbanQuery {
  stageId: string;
  cursor?: string;
}

export const ticketsService = {
  /**
   * Buscar lista de tickets com paginação e filtros
   */
  async getTickets(query?: TicketQuery): Promise<TicketListResponse> {
    const response = await api.get('tickets', { params: query });
    return response.data.data;
  },

  /**
   * Buscar ticket por ID
   */
  async getTicketById(id: string): Promise<TicketResponse> {
    const response = await api.get(`tickets/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo ticket
   */
  async createTicket(data: CreateTicketRequest): Promise<TicketResponse> {
    const response = await api.post('tickets', data);
    return response.data.data;
  },

  /**
   * Atualizar ticket
   */
  async updateTicket(id: string, data: UpdateTicketRequest): Promise<TicketResponse> {
    const response = await api.put(`tickets/${id}`, data);
    return response.data.data;
  },

  /**
   * Deletar ticket
   */
  async deleteTicket(id: string): Promise<void> {
    await api.delete(`tickets/${id}`);
  },

  /**
   * Transferir ticket para outro usuário, equipe e/ou pipeline (ou desatribuir)
   *
   * Endpoint padronizado: `POST /tickets/:id/transfer`
   * Body: `{ toUserId?, toTeamId?, toPipelineId?, toStageId?, reason?, notes? }`
   * — XOR entre user/team, null desatribui, pipeline combinável ou sozinho.
   *
   * Retorna `{ ticket, warnings? }` — `warnings` vem do envelope da resposta
   * (irmão de `data`, não aninhado), populado quando o backend desatribuiu
   * automaticamente o responsável por inelegibilidade no pipeline de destino.
   *
   * `_handleErrorLocally`: os callers (TransferTicketDialog, TicketDetailPage,
   * TicketFormDrawer) têm catch com toast próprio exibindo a mensagem real do
   * backend (`getApiErrorMessage`) — sem o flag, o interceptor global toastaria
   * EM DUPLICATA com o toast local (fix pós-review Task 10).
   */
  async transfer(id: string, body: TransferRequest): Promise<TicketTransferResult> {
    const response = await api.post<{
      success: boolean;
      data: TicketResponse;
      message?: string;
      warnings?: string[];
    }>(`tickets/${id}/transfer`, body, HANDLE_ERROR_LOCALLY);
    return { ticket: response.data.data, warnings: response.data.warnings };
  },

  /**
   * Resolver ticket (`POST /tickets/:id/resolve`, Task 5 do backend).
   *
   * `resolutionSummary` é OPCIONAL — resolver sem resumo move o ticket
   * normalmente. `_handleErrorLocally` porque o caller (`ResolveTicketModal`)
   * trata o erro `NO_RESOLVED_STAGE` (422) localmente pra oferecer o CTA de
   * criação de etapa, em vez do toast genérico do interceptor global.
   */
  async resolveTicket(
    id: string,
    data: { resolutionSummary?: string; internalNotes?: string; customerSatisfaction?: number }
  ): Promise<TicketResponse> {
    const response = await api.post(`tickets/${id}/resolve`, data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Fechar ticket
   */
  async closeTicket(id: string): Promise<TicketResponse> {
    const response = await api.post(`tickets/${id}/close`);
    return response.data.data;
  },

  /**
   * Reabrir ticket
   */
  async reopenTicket(id: string): Promise<TicketResponse> {
    const response = await api.post(`tickets/${id}/reopen`);
    return response.data.data;
  },

  /**
   * Buscar métricas de tickets
   */
  async getMetrics(teamId?: string): Promise<any> {
    const response = await api.get('tickets/metrics', { params: { teamId } });
    return response.data.data;
  },

  /**
   * Mover ticket para outro stage
   */
  async moveTicketToStage(id: string, stageId: string): Promise<TicketResponse> {
    const response = await api.put(`tickets/${id}`, { stageId });
    return response.data.data;
  },

  /**
   * Buscar timeline unificada do ticket (Timeline V2 — Spec A Task 8).
   *
   * `GET /tickets/:id/timeline?cursor=&limit=&includeInternal=` — cursor
   * opaco (base64), ordenado desc (mais recente primeiro). Hoje só retorna
   * itens `kind: 'activity'`; o Plano B pluga `kind: 'email'` sem mudar o
   * shape. Consumido por `useTicketTimeline` (infinite query).
   */
  async getTimeline(
    ticketId: string,
    query?: { cursor?: string; limit?: number; includeInternal?: boolean },
  ): Promise<TicketTimelineResponse> {
    const response = await api.get(`tickets/${ticketId}/timeline`, { params: query });
    return response.data.data;
  },

  /**
   * Mapeia action do backend para type do frontend
   */
  mapActionToType(action: string): string {
    const actionToTypeMap: Record<string, string> = {
      // Comunicação
      'internal_note_added': 'internal_note',
      'comment_added': 'message',
      'message_sent': 'message',
      'message_received': 'message',
      // Status
      'status_changed': 'status_change',
      'ticket_created': 'status_change',
      'ticket_resolved': 'status_change',
      'ticket_closed': 'status_change',
      'ticket_reopened': 'status_change',
      // Prioridade
      'priority_changed': 'priority_change',
      // Atribuição
      'assigned': 'assignment',
      'unassigned': 'assignment',
      'reassigned': 'assignment',
      // SLA
      'sla_breached': 'sla_breach',
      // Anexos
      'attachment_added': 'attachment',
      // Tags
      'tag_added': 'tag_added',
      'tag_removed': 'tag_removed',
    };
    return actionToTypeMap[action] || action || 'message';
  },

  /**
   * Adicionar atividade/comentário ao ticket (composer — aba "Nota Interna"
   * chama sempre com `isInternal: true`; ver `useAddTicketNote`).
   */
  async addTicketActivity(ticketId: string, data: TicketNotePayload & { isInternal: boolean }): Promise<ActivityResponse> {
    const response = await api.post(`tickets/${ticketId}/activities`, {
      content: data.content,
      isInternal: data.isInternal,
      ticketTitle: data.ticketTitle,
      attachments: data.attachments,
    }, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Enviar resposta pública do ticket (composer — aba "Resposta").
   *
   * SEAM único do composer (Plano A — Task 5 no backend): sempre vira
   * activity `comment_added` (`isInternal: false`) via `POST /tickets/:id/reply`.
   * Mesmo shape de anexo de `addTicketActivity`.
   */
  async replyTicket(ticketId: string, body: TicketReplyPayload): Promise<ActivityResponse> {
    const response = await api.post(`tickets/${ticketId}/reply`, body, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Upload de arquivo do composer de tickets — imagem inline (botão imagem
   * do editor rico) OU anexo em staging (clipe). Endpoint genérico
   * `POST /uploads?folder=tickets` (multipart, limite 16MB no backend).
   * `onProgress` (0-100) alimenta a barra de progresso do chip de staging.
   */
  async uploadTicketFile(file: File, onProgress?: (progress: number) => void): Promise<TicketUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{
      success: boolean;
      data: { url: string; key: string; originalName: string; mimeType: string; size: number };
    }>('uploads?folder=tickets', formData, {
      ...HANDLE_ERROR_LOCALLY,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    const { url, originalName, mimeType, size } = response.data.data;
    return { url, filename: originalName, size, mimeType };
  },

  /**
   * Exportar tickets para CSV com filtros aplicados.
   *
   * Dispara o download imediatamente. Se o backend retornar o header
   * `X-Export-Truncated: true`, lança `ExportTruncatedError` APÓS completar
   * o download — o caller deve tratar o erro pra mostrar aviso ao usuário.
   */
  async exportTickets(query: TicketExportQuery): Promise<void> {
    // Serializar arrays como CSV na query string (assigneeIds, tags)
    const params: Record<string, string | boolean> = {};
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        if (value.length > 0) params[key] = value.join(',');
      } else if (typeof value === 'string' && value.length === 0) {
        continue;
      } else {
        params[key] = value as string | boolean;
      }
    }

    const response = await api.get('tickets/export', {
      params,
      responseType: 'blob',
    });

    // Filename: extrair do Content-Disposition, fallback pra default
    let filename = `tickets-export-${new Date().toISOString().split('T')[0]}.csv`;
    const cd = response.headers['content-disposition'] as string | undefined;
    if (cd) {
      const match = /filename="?([^"]+)"?/.exec(cd);
      if (match?.[1]) filename = match[1];
    }

    const blob = new Blob([response.data as BlobPart], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Sinalizar truncamento (download já aconteceu — caller decide UX)
    if (response.headers['x-export-truncated'] === 'true') {
      throw new ExportTruncatedError();
    }
  },

  // ============================================================================
  // KANBAN (SP3)
  // ============================================================================

  /**
   * Buscar board kanban completo (colunas/stages com janela inicial de cards)
   */
  async getKanbanBoard(
    params: TicketKanbanQuery,
  ): Promise<KanbanBoardResponse<TicketKanbanCard, TicketKanbanBoardAggregates>> {
    const response = await api.get('tickets/kanban', { params });
    return response.data.data;
  },

  /** Quem pode ser responsável por ticket do pipeline (participantes em qualquer papel). */
  async getAssigneesForPipeline(pipelineId: string): Promise<ContextOperatorFilterUser[]> {
    const response = await api.get('tickets/assignees/dropdown', { params: { pipelineId } });
    return response.data.data;
  },

  /** Operadores do filtro para o pipeline (participantes + responsáveis por tickets dele). */
  async getOperatorsForPipeline(pipelineId: string, scope?: 'team' | 'all'): Promise<ContextOperatorFilterUser[]> {
    const response = await api.get('tickets/operators/dropdown', {
      params: { pipelineId, ...(scope ? { scope } : {}) },
    });
    return response.data.data;
  },

  /**
   * Buscar página adicional de uma coluna (stage) do kanban (paginação por cursor)
   */
  async getKanbanColumn(params: TicketKanbanColumnQuery): Promise<KanbanColumnPage<TicketKanbanCard>> {
    const response = await api.get('tickets/kanban/column', { params });
    return response.data.data;
  },

  /**
   * Atualizar stageId e/ou kanbanRank de um ticket (move/reorder no kanban)
   */
  async updateTicketKanban(id: string, data: { stageId?: string; kanbanRank?: string }): Promise<TicketResponse> {
    const response = await api.put(`tickets/${id}`, data);
    return response.data.data;
  },
};
